import React from "react";
import styled from "styled-components";

const COMPANY_NAME = "Derby Databases Ltd";
const APP_NAME = "OrderLink";
const CONTACT_EMAIL = "support@derbydatabases.com";
const LAST_UPDATED = "October 2025";

export default function PrivacyPolicy() {
    return (
        <PageWrapper>
            <Header>
                <h1>{APP_NAME} — Privacy Policy</h1>
                <p className="muted">{COMPANY_NAME} — Last updated: {LAST_UPDATED}</p>
            </Header>

            <TabArea>
                <nav aria-label="Table of contents">
                    <TOC>
                        <li><a href="#controller">1. Data Controller</a></li>
                        <li><a href="#data-collected">2. Information We Collect</a></li>
                        <li><a href="#how-we-use">3. How We Use Your Information</a></li>
                        <li><a href="#whatsapp">4. WhatsApp Integration</a></li>
                        <li><a href="#storage">5. Data Storage & Security</a></li>
                        <li><a href="#retention">6. Data Retention</a></li>
                        <li><a href="#rights">7. Your Rights</a></li>
                        <li><a href="#legal-basis">8. Legal Basis for Processing</a></li>
                        <li><a href="#sharing">9. Data Sharing</a></li>
                        <li><a href="#changes">10. Changes to This Policy</a></li>
                        <li><a href="#contact">11. Contact</a></li>
                    </TOC>
                </nav>

                <section id="controller">
                    <SectionTitle>1. Data Controller</SectionTitle>
                    <p>
                        {APP_NAME} is operated by <strong>{COMPANY_NAME}</strong>, registered in the
                        United Kingdom. For privacy enquiries please contact us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
                    </p>
                </section>

                <section id="data-collected">
                    <SectionTitle>2. Information We Collect</SectionTitle>
                    <p>We collect and process the following categories of personal information in connection with your use of the Service:</p>

                    <SubList>
                        <li>
                            <strong>Store information:</strong> store name, store address, store email.
                        </li>
                        <li>
                            <strong>Supplier information:</strong> supplier name, supplier location, contact details (email and phone).
                        </li>
                        <li>
                            <strong>Order data:</strong> product codes, descriptions, quantities and prices.
                        </li>
                        <li>
                            <strong>WhatsApp message data:</strong> payloads and delivery status for messages sent via the Meta
                            WhatsApp Business API. Payloads are retained for debugging and retry purposes (see section 6).
                        </li>
                    </SubList>
                </section>

                <section id="how-we-use">
                    <SectionTitle>3. How We Use Your Information</SectionTitle>
                    <p>We use the information we collect to:</p>
                    <SubList>
                        <li>Facilitate and process orders between stores and suppliers;</li>
                        <li>Send order information to suppliers via WhatsApp when integration is enabled;</li>
                        <li>Maintain accurate business records and provide customer support;</li>
                        <li>Diagnose technical issues, verify delivery, and retry failed messages for reliability.</li>
                    </SubList>
                    <p>We do not use personal data for marketing purposes.</p>
                </section>

                <section id="whatsapp">
                    <SectionTitle>4. WhatsApp Integration</SectionTitle>
                    <p>
                        When WhatsApp integration is enabled, {APP_NAME} uses the <strong>Meta WhatsApp Business API</strong> to
                        transmit messages between stores and suppliers. Message content is transmitted through Meta’s infrastructure
                        and may be processed by Meta in accordance with its terms and policies.
                    </p>
                </section>

                <section id="storage">
                    <SectionTitle>5. Data Storage &amp; Security</SectionTitle>
                    <p>
                        We store data in a Microsoft Azure SQL Database hosted in the United Kingdom. We implement appropriate
                        technical and organisational measures to protect personal data, including encryption at rest and in
                        transit, access controls, and monitoring.
                    </p>
                </section>

                <section id="retention">
                    <SectionTitle>6. Data Retention</SectionTitle>
                    <p>
                        WhatsApp message payloads are retained for up to <strong>30 days</strong> for debugging, delivery verification,
                        and message resend purposes. Order and account data is retained for as long as an account remains active,
                        or as required by applicable law. When data is no longer required it will be securely deleted or
                        anonymised.
                    </p>
                </section>

                <section id="rights">
                    <SectionTitle>7. Your Rights</SectionTitle>
                    <p>
                        Under the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018, you have the
                        right to access, correct, delete, restrict or object to processing of your personal data, and to request
                        data portability where applicable. To exercise these rights contact us at <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
                    </p>
                </section>

                <section id="legal-basis">
                    <SectionTitle>8. Legal Basis for Processing</SectionTitle>
                    <p>
                        We process personal data where necessary to perform the contract with you (to operate the OrderLink
                        service), where we have a legitimate interest (to maintain service reliability, detect and correct
                        faults), and to comply with legal obligations.
                    </p>
                </section>

                <section id="sharing">
                    <SectionTitle>9. Data Sharing</SectionTitle>
                    <p>
                        We do not sell, rent or share personal data with third parties. Data will only be disclosed where required
                        by law or to comply with valid legal requests. Apart from the Meta WhatsApp Business API usage described
                        above, we do not provide personal data to external processors.
                    </p>
                </section>

                <section id="changes">
                    <SectionTitle>10. Changes to This Policy</SectionTitle>
                    <p>
                        We may update this Privacy Policy from time to time. Material changes will be posted in the application or
                        on our website and the date at the top of this page will be revised. Continued use of the Service after
                        changes are posted constitutes acceptance of the updated policy.
                    </p>
                </section>

                <section id="contact">
                    <SectionTitle>11. Contact</SectionTitle>
                    <p>
                        For questions or requests related to this policy please contact:
                        <br />
                        <strong>{COMPANY_NAME}</strong>
                        <br />
                        Email: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
                    </p>
                </section>
            </TabArea>
        </PageWrapper>
    );
}

// Styled components
const PageWrapper = styled.div`
  max-width: 900px;
  margin: 32px auto;
  padding: 24px;
  font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial;
  color: #111827;
  line-height: 1.6;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
`;

const Header = styled.header`
  margin-bottom: 16px;
  h1 {
    margin: 0 0 6px 0;
    font-size: 22px;
  }
  .muted {
    color: #6b7280;
    font-size: 13px;
  }
`;

const TOC = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 18px 0;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  li a {
    text-decoration: none;
    color: #2563eb;
    font-size: 14px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 16px;
  margin: 12px 0;
`;

const SubList = styled.ul`
  margin-left: 0;
  padding-left: 18px;
  li {
    margin-bottom: 8px;
  }
`;

const TabArea = styled.div`
  padding: 20px;
`;
